import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import BackButtonRouter from '../../Components/BackButtonRouter';
import AppConfig from '../../AppConfig';
import APIGet from '../../API/APIGet';
import APIDelete from '../../API/APIDelete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faTrashAlt, faTimes, faDownload } from '@fortawesome/free-solid-svg-icons';

import { toast } from 'react-toastify';

import moment from 'moment-timezone';

import OpenLink from '../../Helper/OpenLink';

export default function Travelers() {
	const PER_PAGE = 15;

	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');

	const [travelers, setTravelers] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [deleteTrigger, setDeleteTrigger] = useState('');

	const requestIdRef = useRef(0);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search), 400);
		return () => clearTimeout(t);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	const fetchTravelers = useCallback(async () => {
		const requestId = ++requestIdRef.current;
		setIsLoading(true);
		setErrorMessage('');

		const result = await APIGet('travelers', {
			filter: (debouncedSearch || '').trim(),
			page,
			perPage: PER_PAGE,
		});

		console.log(result);

		// Prevent stale updates if user typed quickly / pagination changed.
		if (requestId !== requestIdRef.current) return;

		const payload = result && result.data ? result.data : null;
		if (payload && Array.isArray(payload.data)) {
			setTravelers(payload.data);
			setTotalPages(payload.totalPages || 1);
			setIsLoading(false);
			return;
		}

		setTravelers([]);
		setTotalPages(1);
		setIsLoading(false);
		setErrorMessage('ไม่สามารถดึงข้อมูลผู้เดินทางได้');
	}, [debouncedSearch, page]);

	useEffect(() => {
		fetchTravelers();
	}, [fetchTravelers]);

	const paginationItems = useMemo(() => {
		const total = totalPages || 1;
		const current = page || 1;

		if (total <= 1) return [1];

		const items = [];
		const start = Math.max(1, current - 2);
		const end = Math.min(total, current + 2);

		if (start > 1) {
			items.push(1);
			if (start > 2) items.push(null);
		}

		for (let i = start; i <= end; i++) items.push(i);

		if (end < total) {
			if (end < total - 1) items.push(null);
			items.push(total);
		}

		return items;
	}, [page, totalPages]);

	const goToPage = useCallback(
		nextPage => {
			if (nextPage < 1) return;
			if (nextPage > totalPages) return;
			setPage(nextPage);
		},
		[totalPages]
	);

	const formatFullName = useCallback(traveler => {
		const thaiParts = [traveler.thaiFirstName, traveler.thaiMiddleName, traveler.thaiLastName].filter(v => v != null && String(v).trim() !== '');
		if (thaiParts.length) {
			return thaiParts.join(' ');
		}
		const firstName = traveler.firstName || '';
		const middleName = traveler.middleName || '';
		const lastName = traveler.lastName || '';
		return [firstName, middleName, lastName].filter(Boolean).join(' ');
	}, []);

	const confirmDeleteTraveler = useCallback(async travelerId => {
		try {
			// DELETE {{apiUrl}}/travelers/:id
			const result = await APIDelete('travelers/' + travelerId, {});
			if (result && result.status >= 200 && result.status < 300) {
				setDeleteTrigger('');
				toast.success('ลบผู้เดินทางสำเร็จ');
				await fetchTravelers();
			}
		} catch (e) {
			console.log(e);
		}
	}, []);

	const onGetAllTravelers = useCallback(() => {
		try {
			OpenLink.getTravelersExcel();
		} catch (e) {
			console.log(e);
		}
	}, [OpenLink]);

	return (
		<div className="travelers-page">
			{/* <div className="travelers-hero" /> */}
			<div className="container travelers-content" style={{ paddingTop: 30 }}>
				<BackButtonRouter title="ดูทั้งหมด" link={AppConfig.appPath + '/dashboard'} />
				<div className="columns is-vcentered travelers-title-row">
					<div className="column is-6">
						<h1 className="title is-3">Travelers</h1>
					</div>
					<div className="column is-6 has-text-right">
						<button
							className="button"
							style={{
								backgroundColor: '#ff6e12',
								color: 'white',
								border: 'none',
								borderRadius: 5,
								padding: '10px 20px',
								fontSize: 16,
								fontWeight: 500,
								display: 'inline-flex',
								alignItems: 'center',
								gap: 10,
								marginRight: 10,
							}}
							onClick={onGetAllTravelers}
						>
							<FontAwesomeIcon icon={faDownload} />
							All
						</button>
						<Link to={AppConfig.appPath + '/travelers/new'} className="button is-success travelers-add-button">
							<FontAwesomeIcon icon={faPlus} style={{ fontSize: 12 }} />
							Traveler
						</Link>
					</div>
				</div>

				<div className="field travelers-search">
					<div className="control has-icons-right">
						<input
							className="input"
							type="text"
							placeholder="ค้นหาชื่อ, เบอร์โทร, emails"
							value={search}
							onChange={event => setSearch(event.target.value)}
						/>
						<span className="icon is-right">
							<FontAwesomeIcon icon={faSearch} />
						</span>
					</div>
				</div>

				<div className="table-container" style={{ borderRadius: 10 }}>
					<table className="table is-fullwidth is-hoverable travelers-table">
						<thead className="table-header-travelers">
							<tr>
								<th width="22%" className="travelers-table-header-left">
									ชื่อ-นามสกุล
								</th>
								<th width="18%">เบอร์โทร</th>
								<th width="18%">emails</th>
								<th width="8%">อายุ</th>
								<th width="12%">สร้างเมื่อ</th>
								<th width="5%">ลบ</th>
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
								<tr>
									<td colSpan="7">กำลังโหลด...</td>
								</tr>
							) : errorMessage ? (
								<tr>
									<td colSpan="7">{errorMessage}</td>
								</tr>
							) : travelers.length ? (
								travelers.map(item => (
									<tr key={item.id}>
										<td>
											<Link className="travelers-row-link" to={AppConfig.appPath + '/travelers/' + item.id}>
												{formatFullName(item) || '-'}
											</Link>
										</td>
										<td>{item.telephone || '-'}</td>
										<td>{item.email || '-'}</td>
										<td>{item.age != null && item.age !== '' ? item.age : '-'}</td>
										<td>{item.createdAt ? moment(item.createdAt).format('D MMM YYYY HH:mm') : '-'}</td>
										<td>
											{deleteTrigger === item.id ? (
												<div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
													<button
														type="button"
														className="button is-small"
														style={{
															backgroundColor: '#DC1818',
															borderColor: '#DC1818',
															color: '#fff',
															opacity: false ? 0.7 : 1,
														}}
														onClick={() => confirmDeleteTraveler(item.id)}
														disabled={false}
														aria-label="ลบ"
														title="ลบ"
													>
														<FontAwesomeIcon icon={faTrashAlt} />
													</button>
													<button
														type="button"
														className="button is-small"
														style={{
															backgroundColor: '#22c65b',
															borderColor: '#22c65b',
															color: '#fff',
															opacity: false ? 0.7 : 1,
														}}
														onClick={() => setDeleteTrigger('')}
														disabled={false}
														aria-label="ยกเลิก"
														title="ยกเลิก"
													>
														<FontAwesomeIcon icon={faTimes} />
													</button>
												</div>
											) : (
												<button
													className="button is-small"
													style={{ backgroundColor: 'transparent', borderColor: 'transparent', color: '#DC1818' }}
													onClick={() => setDeleteTrigger(item.id)}
												>
													<FontAwesomeIcon icon={faTrashAlt} />
												</button>
											)}
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan="7">ไม่มีข้อมูลผู้เดินทาง</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<nav className="pagination is-small is-right travelers-pagination" role="navigation" aria-label="pagination">
					<a
						className={'pagination-previous' + (page <= 1 ? ' is-disabled' : '')}
						onClick={page <= 1 ? undefined : () => goToPage(page - 1)}
						aria-disabled={page <= 1}
					>
						&lt;
					</a>
					<a
						className={'pagination-next' + (page >= totalPages ? ' is-disabled' : '')}
						onClick={page >= totalPages ? undefined : () => goToPage(page + 1)}
						aria-disabled={page >= totalPages}
					>
						&gt;
					</a>
					<ul className="pagination-list">
						{paginationItems.map((item, idx) =>
							item == null ? (
								<li key={'ellipsis-' + idx}>
									<span className="pagination-ellipsis">&hellip;</span>
								</li>
							) : (
								<li key={'page-' + item}>
									<a
										className={'pagination-link' + (item === page ? ' is-current' : '')}
										aria-current={item === page ? 'page' : undefined}
										onClick={() => goToPage(item)}
									>
										{item}
									</a>
								</li>
							)
						)}
					</ul>
				</nav>
			</div>
		</div>
	);
}
